import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "addressError", "addressInput", "zipCodeInput", "cityInput", "latInput", "lngInput", "mapAddress", "mapCanvas",
    "radiusInput"
  ]
  static outlets = ["form--google-autocomplete-input"]

  async connect() {
    if (!this.hasMapCanvasTarget || !this.hasMapCoordinates()) return

    await this.refreshMap()
  }

  updateDeliveryCircle() {
    if (!this.hasMapAddressTarget || !this.hasMapCanvasTarget) return

    this.mapAddressTarget.dataset.circleRadius = this.radiusInputTarget.value
    this.refreshMap()
  }

  clearLocationInputsWhenBlank(event) {
    if (event.currentTarget.value.trim() !== "") return

    this.addressInputTarget.value = ""
    this.zipCodeInputTarget.value = ""
    this.cityInputTarget.value = ""
    this.latInputTarget.value = ""
    this.lngInputTarget.value = ""
  }

  formGoogleAutocompleteInputOutletConnected(outlet) {
    outlet.selectGooglePlace = this.selectGooglePlace.bind(this)
  }

  selectGooglePlace(placeData) {
    this.placeData = placeData
    this.formGoogleAutocompleteInputOutlet.inputTarget.value = this.inputValue()
    this.fillFormInputsWithPlaceData()
    this.clearAddressError()
    this.updateMapAddress()
  }

  inputValue() {
    return this.placeData.formatted_address
  }

  fillFormInputsWithPlaceData() {
    this.addressInputTarget.value = this.formattedAddress()
    this.zipCodeInputTarget.value = this.findAddressComponent("postal_code")?.long_name || null
    this.cityInputTarget.value = this.cityName()
    this.latInputTarget.value = this.placeData.geometry.location.lat()
    this.lngInputTarget.value = this.placeData.geometry.location.lng()
  }

  updateMapAddress() {
    if (!this.hasMapAddressTarget || !this.hasMapCanvasTarget) return

    this.mapAddressTarget.dataset.lat = String(this.latInputTarget.value)
    this.mapAddressTarget.dataset.lng = String(this.lngInputTarget.value)
    this.mapAddressTarget.dataset.title = this.mapTitle()
    this.refreshMap()
  }

  clearAddressError() {
    this.formGoogleAutocompleteInputOutlet.inputTarget.classList.remove("is-invalid")

    if (this.hasAddressErrorTarget) this.addressErrorTarget.remove()
  }

  mapTitle() {
    const city = [this.zipCodeInputTarget.value, this.cityInputTarget.value].filter(Boolean).join(" ")

    return [this.addressInputTarget.value, city].filter(Boolean).join(", ")
  }

  formattedAddress() {
    const streetNumber = this.findAddressComponent("street_number")?.long_name || ""
    const route = this.findAddressComponent("route")?.long_name || ""
    const address = `${streetNumber} ${route}`.trim()

    return address || this.placeData.formatted_address
  }

  cityName() {
    return this.findAddressComponent("locality")?.long_name ||
      this.findAddressComponent("postal_town")?.long_name ||
      this.findAddressComponent("administrative_area_level_3")?.long_name ||
      this.findAddressComponent("administrative_area_level_2")?.long_name ||
      null
  }

  findAddressComponent(type) {
    return this.placeData.address_components?.find(component => component.types.includes(type))
  }

  hasMapCoordinates() {
    return this.hasMapAddressTarget && this.mapAddressTarget.dataset.lat && this.mapAddressTarget.dataset.lng
  }

  async refreshMap() {
    await Promise.all([
      google.maps.importLibrary("maps"),
      google.maps.importLibrary("marker")
    ])

    delete this.mapCanvasTarget.dataset.productMapTarget

    requestAnimationFrame(() => {
      this.mapCanvasTarget.dataset.productMapTarget = "map"
    })
  }
}
